<template>
  <div
    class="rs-rate-input"
    :class="inputClasses"
  >
    <label
      v-if="label"
      :for="id"
      class="align-items-center"
    >{{ label }}
      <sup
        v-if="required"
        class="mt-2"
      >*</sup>
      <!-- Optional inline marker rendered after the label text (e.g. an "Override" badge). -->
      <slot name="label-suffix" />
    </label>
    <Field
      v-slot="{ errors }"
      v-model="input"
      class="veeValidate"
      as="div"
      :name="validationName"
      :rules="rules"
    >
      <div
        v-if="imported"
        class="import-tooltip"
        :class="{ 'import-tooltip--open': importHover }"
      >
        <p>This field is managed by <strong>[Data Source]</strong>.<br>Changing this will be temporary.</p>
      </div>
      <div class="input-group" role="group">
        <div class="input-group-prepend prepend">
          <span class="dollarSign prepend bg-1">
            $
          </span>
        </div>
        <input
          :id="id"
          v-model="input"
          type="text"
          class="form-control"
          :max="max"
          :min="min"
          :step="step"
          :name="name"
          :required="required"
          :disabled="disabled || (imported && isUnlocked == false)"
          :placeholder="placeholder"
          :readonly="readonly || (imported && isUnlocked == false)"
          @change="onInputChange(($event.target as HTMLInputElement).value)"
          @keypress="ignoreNonNumericKeypress"
          @keypress.enter="handleEnter"
          @blur="(data) => $emit('blur', data)"
          @focus="(data) => $emit('focus', data)"
        >
      </div>
      <div
        v-if="errors.length || customErrorMessage"
        class="text--error"
      >
        {{ errors[0] || customErrorMessage }}
      </div>
    </Field>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { Field, defineRule } from 'vee-validate';

const isFloatNumber = (value: string | number | null | undefined): boolean => {
  // The field is optional: an empty entry is valid, only a present value must be
  // numeric (and >= 1). vee-validate v3 skipped rules on empty values automatically;
  // v4 runs them, so an absent value now arrives here as null/undefined and must be
  // treated as valid to match the Vue 2 baseline (otherwise String(null) → "null"
  // fails the regex and blocks saving a property that has no pet deposit).
  if (value === null || value === undefined) return true;
  const trimValue = typeof value === 'string' ? value.replace(/\s/g, '') : String(value);
  if (trimValue === '') return true;
  if (Number(trimValue) < 1) {
    return false;
  }
  return /^(?![.,]?$)\d*[.,]?\d{0,2}$/im.test(trimValue);
};
defineRule('floatNumbers', (value: string | number) => isFloatNumber(value) || 'Input value should be numeric or greater than 0');

// Per-instance fallback so each field's vee-validate `<Field>` gets a unique name
// (see the `validationName` computed / RsFormInput for the collision this avoids).
let uid = 0;

export default defineComponent({
  name: 'RsFormRateInput',
  components: {
    Field,
  },
  props: {
    rules: [String, Object],
    id: {
      type: String,
      default: '',
    },
    label: String,
    placeholder: String,
    required: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    listingScoresHeart: {
      type: Boolean,
      default: false,
    },
    customErrorMessage: {
      type: String as () => string,
      required: false,
    },
    min: {
      type: Number,
    },
    max: {
      type: Number,
    },
    step: {
      type: Number,
    },
    wrap: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Number],
      default: '',
    },
    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: [String, Number],
      default: undefined,
    },
    imported: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: 'input',
    },
    /**
     * Color variants:  success | warning | error | error-alt
     *
     */
    variant: {
      type: String,
      validator: (prop: string) => [
        'success',
        'warning',
        'error',
        'error-alt',
      ].includes(prop),
    },
  },

  emits: ['blur', 'enter', 'focus', 'input', 'update:modelValue'],
  data() {
    uid += 1;
    return {
      isUnlocked: false as boolean,
      importHover: false as boolean,
      input: (this.modelValue !== undefined ? this.modelValue : this.value) as string | number,
      fallbackId: `rs-rate-input-${uid}`,
    };
  },
  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): string | number {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    /**
     * vee-validate v4 merges `<Field>`s that share a `name` within the same form,
     * bleeding one field's value into another. `name` defaults to 'input', so
     * unlabelled rate inputs would collide; fall back to a per-instance id.
     */
    validationName(): string {
      if (this.label) return this.label;
      if (this.name && this.name !== 'input') return this.name;
      return this.fallbackId;
    },
    inputClasses(): Record<string, boolean> {
      return {
        'rs-input--success': this.variant === 'success',
        'rs-input--warning': this.variant === 'warning',
        'rs-input--error': this.variant === 'error',
        'rs-input--error-alt': this.variant === 'error-alt',
        'rs-input--imported': this.imported,
        'rs-input--unlocked': this.isUnlocked,
      };
    },
  },
  watch: {
    input() {
      if (this.input !== 'NaN') {
        this.$emit('input', this.input);
        this.$emit('update:modelValue', this.input);
      }
    },
    boundValue() {
      if (this.boundValue !== 'NaN') {
        this.input = this.boundValue;
      }
    },

  },
  methods: {
    onInputChange(val: string): void {
      if (val !== '') {
        this.input = val;
        this.$emit('input', this.input);
        this.$emit('update:modelValue', this.input);
        this.$forceUpdate();
      } else {
        this.$emit('input', null);
        this.$emit('update:modelValue', null);
      }
    },
    ignoreNonNumericKeypress(e: KeyboardEvent): boolean {
      const evt = (e) || window.event;
      const charCode = (evt.which ? evt.which : evt.keyCode);

      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        evt.preventDefault();
        return false;
      }
      return true;
    },
    toggleState(): void {
      this.isUnlocked = !this.isUnlocked;
    },
    handleEnter(data: Event): void {
      this.$emit('enter', data);
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
.rs-rate-input {
  .prepend {
    width: 41px;
    height: 40px;
  }
  .input {
    padding: $input-padding;
    width: 100%;
    height: 40px;
    background-color: $white;
    line-height: $input-line-height;
    border-radius: $input-radius;
    border: $input-border;
    color: $input-color;
    font-size: $input-font-size;
    box-sizing: border-box;
    &:focus {
      box-shadow: 0 0 0 3px rgba($info, 0.1);
      border-color: $info;
    }
    @include placeholder {
      color: rgba($dark-gray, 0.4);
    }
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
  .dollarSign {
    transition: all 0.2s ease;
    background-color: $white;
    font-size: $form-label-font-size;
    display: flex;
    font-weight: $form-label-font-weight;
    border-radius: $input-radius 0px 0px $input-radius;
    border: $input-border;
    align-items: center;
    justify-content: center;
  }
}
</style>
